/**
 * Scheduler Controls — admin/superadmin UI for the daily lifecycle-email
 * dispatcher (see backend `core/scheduler.py`).
 *
 * Renders three things:
 *   1. Current status card — paused sentinel + cutover ISO + next cron
 *      fire + last run summary (counts, timestamp).
 *   2. Pause / Unpause toggle. Optional reason field when pausing.
 *   3. Run Now button with a hard 60s client-side re-click guard.
 *      Result of each manual run is shown right below the button.
 *
 * Safety layers reminder (backend enforces these regardless of the UI):
 *   • Paused sentinel → run-now returns {paused:true} without touching data.
 *   • Advisory lock   → concurrent runs return {locked:true}.
 *   • Atomic per-doc claim before every send.
 *   • Cutover ISO exclusion.
 *
 * This page is read-only if you don't have a valid admin/superadmin
 * session — the underlying endpoints all sit behind `require_admin`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle, CheckCircle2, Clock, Loader2, PauseCircle, PlayCircle,
  RefreshCw, ShieldCheck, TimerReset,
} from "lucide-react";
import { toast } from "sonner";

const COOLDOWN_MS = 60_000;

export default function SchedulerControlsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRunResult, setLastRunResult] = useState(null); // {summary, at}
  const [pauseReason, setPauseReason] = useState("");
  const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  // ── Ticker so the cooldown countdown re-renders every second ─────
  const tickerRef = useRef(null);
  useEffect(() => {
    tickerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickerRef.current);
  }, []);

  // Persist cooldown across refreshes — a 60s cooldown that disappears
  // on tab-close would defeat the whole point. Stored in localStorage.
  useEffect(() => {
    const raw = localStorage.getItem("citydooh_scheduler_cooldown_ends");
    if (raw) {
      const t = parseInt(raw, 10);
      if (!Number.isNaN(t) && t > Date.now()) setCooldownEndsAt(t);
    }
  }, []);
  useEffect(() => {
    if (cooldownEndsAt > 0) {
      localStorage.setItem("citydooh_scheduler_cooldown_ends", String(cooldownEndsAt));
    }
  }, [cooldownEndsAt]);

  const cooldownRemaining = Math.max(0, cooldownEndsAt - now);
  const cooldownActive = cooldownRemaining > 0;

  // ── Fetch status ─────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.adminSchedulerStatus();
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load scheduler status");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Actions ──────────────────────────────────────────────────────
  const doPause = async () => {
    setPausing(true);
    try {
      const r = await api.adminSchedulerPause({ reason: pauseReason.trim() || undefined });
      setStatus(r.data.status);
      setPauseReason("");
      toast.success("Scheduler paused. The daily job will not fire.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to pause scheduler");
    } finally {
      setPausing(false);
    }
  };

  const doResume = async () => {
    setResuming(true);
    try {
      const r = await api.adminSchedulerResume();
      setStatus(r.data.status);
      toast.success("Scheduler unpaused. Next fire is 03:00 UTC or the next Run Now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unpause scheduler");
    } finally {
      setResuming(false);
    }
  };

  const doRunNow = async () => {
    if (cooldownActive) {
      toast.error(`Cooldown active — wait ${Math.ceil(cooldownRemaining / 1000)}s before re-running.`);
      return;
    }
    // Start the cooldown IMMEDIATELY so a double-click cannot fire twice
    // before the server responds.
    const ends = Date.now() + COOLDOWN_MS;
    setCooldownEndsAt(ends);
    setRunning(true);
    setLastRunResult(null);
    try {
      const r = await api.adminSchedulerRunNow();
      setLastRunResult({ summary: r.data?.summary, at: new Date().toISOString() });
      // Refresh status so `last_run` on the server side is reflected.
      await load();
      // Toast: mention the important cases
      const s = r.data?.summary || {};
      if (s.paused) {
        toast.info("Scheduler is currently PAUSED — no work done.");
      } else if (s.locked) {
        toast.info("Another run is already in-flight — this invocation short-circuited.");
      } else {
        const sends = (s.vendors?.day1 || 0) + (s.vendors?.day13 || 0)
                    + (s.vendors?.day15 || 0) + (s.leads?.sent || 0);
        toast.success(`Run complete — ${sends} email${sends === 1 ? "" : "s"} sent.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-scheduler-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <TimerReset className="h-5 w-5 text-primary" />
            Scheduler Controls
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Controls for the daily lifecycle-email dispatcher (trial day 1 /
            13 / 15 nudges + 12-hour lead follow-up). Backend enforces the
            paused sentinel, advisory lock, atomic claim, and cutover
            exclusion regardless of what happens in this UI.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="scheduler-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {loading && !status ? (
        <Card className="p-8">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
          </div>
        </Card>
      ) : status ? (
        <>
          <StatusCard status={status} />
          <PauseCard
            status={status}
            pausing={pausing}
            resuming={resuming}
            pauseReason={pauseReason}
            setPauseReason={setPauseReason}
            doPause={doPause}
            doResume={doResume}
          />
          <RunNowCard
            status={status}
            running={running}
            cooldownActive={cooldownActive}
            cooldownRemaining={cooldownRemaining}
            doRunNow={doRunNow}
            lastRunResult={lastRunResult}
          />
        </>
      ) : (
        <Card className="p-6" data-testid="scheduler-error">
          <p className="text-sm text-red-500">Could not load scheduler status.</p>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   StatusCard — current sentinel state, cutover, next fire, last run
   ────────────────────────────────────────────────────────────────*/

const StatusCard = ({ status }) => {
  const paused = !!status.paused;
  const badgeStyle = paused
    ? "bg-amber-500/15 text-amber-500 border-amber-500/40"
    : "bg-emerald-500/15 text-emerald-500 border-emerald-500/40";
  const lastRun = status.last_run || {};
  return (
    <Card className="p-5 space-y-5" data-testid="scheduler-status-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Current status
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Snapshot fetched at {fmt(status.now_iso)}.
          </p>
        </div>
        <Badge className={`border ${badgeStyle} uppercase tracking-wide font-bold`} data-testid="scheduler-state-badge">
          {paused ? "PAUSED" : "ACTIVE"}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <KV label="Paused sentinel" value={paused ? "true" : "false"} testid="kv-paused" />
        {paused && (
          <>
            <KV label="Paused reason"  value={status.paused_reason || "—"} testid="kv-paused-reason" />
            <KV label="Paused by"      value={status.paused_by || "—"} testid="kv-paused-by" />
          </>
        )}
        {!paused && (
          <>
            <KV label="Unpaused at"    value={fmt(status.unpaused_at)} testid="kv-unpaused-at" />
            <KV label="Unpaused by"    value={status.unpaused_by || "—"} testid="kv-unpaused-by" />
          </>
        )}
        <KV
          label="Cutover ISO"
          value={status.cutover_iso}
          hint={`source: ${status.cutover_source || "?"}`}
          mono
          testid="kv-cutover"
        />
        <KV
          label="Next scheduled fire"
          value={fmt(status.next_scheduled_at)}
          hint="03:00 UTC daily"
          testid="kv-next-fire"
        />
        <KV
          label="Advisory lock"
          value={status.lock_held ? `HELD since ${fmt(status.lock_held_at)}` : "free"}
          testid="kv-lock"
        />
      </div>

      <div>
        <p className="text-xs uppercase text-muted-foreground tracking-wider font-semibold mb-2">
          Last run summary
        </p>
        {Object.keys(lastRun).length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No runs recorded yet.</p>
        ) : (
          <div className="rounded-md border border-border bg-secondary/50 p-3 space-y-2 text-sm">
            {lastRun.paused && (
              <p className="text-amber-500 font-semibold">Last run: PAUSED — no-op.</p>
            )}
            {lastRun.locked && (
              <p className="text-amber-500 font-semibold">Last run: LOCKED — another run was active.</p>
            )}
            {lastRun.vendors && lastRun.leads && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <StatTile label="day 1 sent"        value={lastRun.vendors.day1} testid="stat-day1" />
                  <StatTile label="day 13 sent"       value={lastRun.vendors.day13} testid="stat-day13" />
                  <StatTile label="day 15 sent"       value={lastRun.vendors.day15} testid="stat-day15" />
                  <StatTile label="lead follow-up"    value={lastRun.leads.sent} testid="stat-lead-sent" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <StatTile label="vendors scanned"   value={lastRun.vendors.scanned}       muted testid="stat-vendors-scanned" />
                  <StatTile label="cutover skipped"   value={lastRun.vendors.cutover_skipped} muted testid="stat-vendors-cutover" />
                  <StatTile label="leads scanned"     value={lastRun.leads.scanned}         muted testid="stat-leads-scanned" />
                  <StatTile label="lead no-email"     value={lastRun.leads.skipped_no_email} muted testid="stat-lead-no-email" />
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground pt-1">
              Recorded at {fmt(lastRun.ran_at)}
              {lastRun.cutover ? ` · cutover was ${lastRun.cutover}` : ""}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PauseCard — pause/unpause toggle with optional reason field
   ────────────────────────────────────────────────────────────────*/

const PauseCard = ({ status, pausing, resuming, pauseReason, setPauseReason, doPause, doResume }) => {
  const paused = !!status.paused;
  return (
    <Card className="p-5 space-y-4" data-testid="scheduler-pause-card">
      <div>
        <h2 className="font-heading text-base font-semibold flex items-center gap-2">
          {paused ? <PauseCircle className="h-4 w-4 text-amber-500" /> : <PlayCircle className="h-4 w-4 text-emerald-500" />}
          Pause / Unpause
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
          Flips <code>db.settings.lifecycle_emails_paused</code>. When paused,
          both the natural 03:00 UTC cron and any manual Run Now become no-ops
          (returning <code>{"{paused: true}"}</code> without touching data).
          Nothing else on the platform is affected.
        </p>
      </div>

      {paused ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={doResume}
            disabled={resuming}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="scheduler-unpause-btn"
          >
            {resuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            <span className="ml-2">Unpause scheduler</span>
          </Button>
          <p className="text-xs text-muted-foreground">
            Unpausing only removes the pause — the next actual fire is either the 03:00 UTC cron
            or an explicit Run Now.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Pause reason (optional)
          </Label>
          <Textarea
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            placeholder="e.g. investigating an inbox bounce — do not fire until fixed"
            rows={2}
            className="max-w-xl"
            data-testid="scheduler-pause-reason"
          />
          <div className="pt-1">
            <Button
              onClick={doPause}
              disabled={pausing}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="scheduler-pause-btn"
            >
              {pausing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PauseCircle className="h-4 w-4" />}
              <span className="ml-2">Pause scheduler</span>
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RunNowCard — manual trigger with 60s guard + result display
   ────────────────────────────────────────────────────────────────*/

const RunNowCard = ({ status, running, cooldownActive, cooldownRemaining, doRunNow, lastRunResult }) => {
  const paused = !!status.paused;
  const cdSeconds = Math.ceil(cooldownRemaining / 1000);
  const disabled = running || cooldownActive;
  return (
    <Card className="p-5 space-y-4" data-testid="scheduler-runnow-card">
      <div>
        <h2 className="font-heading text-base font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Run Now (manual trigger)
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
          Fires the exact same code path as the 03:00 UTC cron. There is a
          60-second client-side cooldown after each click to prevent
          double-fires while the previous invocation is in-flight — the
          server-side advisory lock is the real safety net, this cooldown
          just discourages panic-clicking. Cooldown persists across refreshes.
        </p>
      </div>

      {paused && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-start gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-500">Scheduler is currently paused.</p>
            <p className="text-xs text-muted-foreground">
              Run Now will return <code>{"{paused: true}"}</code> and do no work
              until the sentinel is flipped off.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={doRunNow}
          disabled={disabled}
          className="bg-primary hover:bg-primary/90"
          data-testid="scheduler-run-now-btn"
          aria-disabled={disabled}
        >
          {running ? (
            <><Loader2 className="h-4 w-4 animate-spin" /><span className="ml-2">Running…</span></>
          ) : cooldownActive ? (
            <><Clock className="h-4 w-4" /><span className="ml-2">Wait {cdSeconds}s</span></>
          ) : (
            <><PlayCircle className="h-4 w-4" /><span className="ml-2">Run Now</span></>
          )}
        </Button>
        {cooldownActive && (
          <p className="text-xs text-muted-foreground" data-testid="scheduler-cooldown-note">
            Re-click guard: {cdSeconds}s remaining. Even if you refresh this page
            or open a new tab, the guard persists until it expires.
          </p>
        )}
      </div>

      {lastRunResult && (
        <ResultPanel result={lastRunResult} />
      )}
    </Card>
  );
};

const ResultPanel = ({ result }) => {
  const s = result.summary || {};
  const isCase = (k) => Object.prototype.hasOwnProperty.call(s, k);
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2" data-testid="scheduler-run-result">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Last manual run result — {fmt(result.at)}</p>
      </div>
      {isCase("paused") && s.paused && (
        <p className="text-sm text-amber-500">Result: <strong>PAUSED</strong> — no work done.</p>
      )}
      {isCase("locked") && s.locked && (
        <p className="text-sm text-amber-500">Result: <strong>LOCKED</strong> — another run was in-flight.</p>
      )}
      {s.vendors && s.leads && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatTile label="day 1 sent"        value={s.vendors.day1}  emphasis testid="live-stat-day1" />
            <StatTile label="day 13 sent"       value={s.vendors.day13} emphasis testid="live-stat-day13" />
            <StatTile label="day 15 sent"       value={s.vendors.day15} emphasis testid="live-stat-day15" />
            <StatTile label="lead follow-up"    value={s.leads.sent}    emphasis testid="live-stat-lead" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatTile label="vendors scanned"    value={s.vendors.scanned}         muted />
            <StatTile label="vendors cutover"    value={s.vendors.cutover_skipped} muted />
            <StatTile label="leads scanned"      value={s.leads.scanned}           muted />
            <StatTile label="lead no-email"      value={s.leads.skipped_no_email}  muted />
          </div>
          <pre
            className="text-[11px] font-mono text-muted-foreground bg-secondary/60 rounded p-2 overflow-x-auto"
            data-testid="scheduler-result-raw"
          >
{JSON.stringify(s, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
};

/* ─── Reusable atoms ────────────────────────────────────────────── */

const KV = ({ label, value, hint, mono, testid }) => (
  <div className="flex flex-col md:flex-row md:justify-between gap-0 md:gap-3 border-b border-border/50 py-1.5">
    <span className="text-muted-foreground">{label}</span>
    <span className={`text-right font-medium break-all ${mono ? "font-mono text-xs" : ""}`} data-testid={testid}>
      {value ?? "—"}
      {hint && <span className="text-muted-foreground text-xs block md:inline md:ml-2">({hint})</span>}
    </span>
  </div>
);

const StatTile = ({ label, value, muted, emphasis, testid }) => (
  <div
    className={`rounded-md border px-3 py-2 ${
      muted ? "border-border/50 bg-secondary/40"
        : emphasis ? "border-primary/40 bg-primary/10"
        : "border-border bg-secondary"
    }`}
    data-testid={testid}
  >
    <p className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</p>
    <p className={`font-heading font-semibold tabular-nums ${emphasis ? "text-2xl" : "text-xl"}`}>
      {value ?? 0}
    </p>
  </div>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
